// install (please try to align the version of installed @nivo packages)
// yarn add @nivo/pie
import { Pie, ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import useFetch from '../../api/useFetch';
import {
  deliveryAssignmentDailyPreviewGetApi,
  DeliveryPreviewType,
  productionBetweenDaysGetApi,
  ProductionSimpleType,
} from '../../api/apiConfig';
import { useEffect, useState } from 'react';
import {
  getDayOfWeekShort,
  getYearMonthDateFromDate,
} from '../../function/replaceString';
import styled, { useTheme } from 'styled-components';
import { IoIosArrowBack } from 'react-icons/io';
import { IoIosArrowForward } from 'react-icons/io';
import { ResponsiveRadialBar } from '@nivo/radial-bar';

type ChartDataType = {
  x: string;
  y: number;
};

type ChartGraphType = {
  id: string; //라이브러리 요구사항
  itemId: number; //pk값
  goal: number;
  data: ChartDataType[];
};

type ChartGroupType = {
  workbayId: number;
  chartGraphList: ChartGraphType[];
};

const workbayList = [
  {
    id: 1,
    name: '양초작업장',
  },
  {
    id: 2,
    name: '화장지작업장',
  },
  {
    id: 3,
    name: '핸드타올작업장',
  },
];

export const TodaysProduction = () => {
  const theme = useTheme();

  //차트 데이터를 작업장별로 묶어둘 state
  const [chartGroupList, setChartGroupList] = useState<ChartGroupType[]>([]);

  //현재 선택된 차트데이터
  const [selectedChartGroup, setSelectedChartGroup] =
    useState<ChartGroupType | null>(null);

  /** 생산리스트 조회 */
  const { fetch: fetchProductionBetweenDaysGetApi } = useFetch({
    fetchFunction: productionBetweenDaysGetApi,
    onSuccess: (data: ProductionSimpleType[]) => groupingData(data),
  });

  useEffect(() => {
    const nowDate = getYearMonthDateFromDate(new Date()).dateString;
    fetchProductionBetweenDaysGetApi({
      startDate: nowDate,
      endDate: nowDate,
    });
  }, []);

  useEffect(() => {
    if (chartGroupList.length < 1) return;
    setSelectedChartGroup(chartGroupList[0]);
  }, [chartGroupList]);

  //api response(생산리스트) 작업장별 그룹화 및 생산품별 합산
  function groupingData(data: ProductionSimpleType[]) {
    let groupedDataList: ChartGroupType[] = [];

    data.forEach((production) => {
      const existingGroup = groupedDataList.find(
        (groupedData) => groupedData.workbayId === production.workbayId
      );

      if (existingGroup) {
        // 기존 그룹이 있으면 dataList 업데이트
        const existingItem = existingGroup.chartGraphList.find(
          (data) => data.itemId === production.itemId
        );

        if (existingItem) {
          // 같은 itemId가 있으면 수량 추가. 0번째 배열은 생산량, 1번째 배열은 목표수량-생산량
          const newProduceQty = production.qty;
          const newRemainingQty = production.productionGoal - production.qty;
          existingItem.goal += production.productionGoal;
          existingItem.data[0].y += newProduceQty;
          existingItem.data[1].y += newRemainingQty;
        } else {
          // 새로운 itemId면 existingGroup에 추가
          const producedQty = production.qty;
          let remainingQty = production.productionGoal - production.qty;
          if (remainingQty < 0) remainingQty = 0;
          const chartData: ChartDataType[] = [
            {
              x: '생산수량',
              y: producedQty,
            },
            {
              x: '남은수량',
              y: remainingQty,
            },
          ];
          const newItem: ChartGraphType = {
            id: production.prodDes,
            itemId: production.itemId,
            goal: production.productionGoal,
            data: chartData,
          };
          existingGroup.chartGraphList.push(newItem);
        }
      } else {
        //새로운 아이템 생성
        const producedQty = production.qty;
        let remainingQty = production.productionGoal - production.qty;
        if (remainingQty < 0) remainingQty = 0;
        const chartData: ChartDataType[] = [
          {
            x: '생산수량',
            y: producedQty,
          },
          {
            x: '남은수량',
            y: remainingQty,
          },
        ];
        const newItem: ChartGraphType = {
          id: production.prodDes,
          goal: production.productionGoal,
          itemId: production.itemId,
          data: chartData,
        };
        // 새로운 그룹 생성
        const newGroup: ChartGroupType = {
          workbayId: production.workbayId,
          chartGraphList: [newItem],
        };
        groupedDataList.push(newGroup);
      }
    });

    groupedDataList.forEach((groupData) => {
      groupData.chartGraphList.sort((a, b) => a.goal - b.goal);
    });

    setChartGroupList(groupedDataList);
  }

  //작업장 변경하기(화살표)
  function changeWorkbayGroup(type: 1 | -1) {
    const currentIndex = chartGroupList.findIndex(
      (data) => data.workbayId === selectedChartGroup?.workbayId
    );
    let newIndex;
    if (type === 1) {
      if (currentIndex < chartGroupList.length - 1) {
        newIndex = currentIndex + 1;
      } else {
        newIndex = 0;
      }
    } else {
      if (currentIndex === 0) {
        newIndex = chartGroupList.length - 1;
      } else {
        newIndex = currentIndex - 1;
      }
    }
    setSelectedChartGroup(chartGroupList[newIndex]);
  }

  return (
    //container size fix
    <div
      style={{
        width: '100%',
        height: '268px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {selectedChartGroup !== null ? (
        <>
          {/* ResponsivePie 반응형 차트 사이즈 고정용 ChartSizeFixer */}
          <ChartSizeFixer>
            <ResponsiveRadialBar
              data={selectedChartGroup.chartGraphList}
              valueFormat='>-.2f'
              padding={0.4}
              innerRadius={0.15}
              colors={['hsl(135 58% 60% / 1)', '#b1b1b1b5']}
              cornerRadius={0}
              margin={{ top: 20, right: 90, bottom: 60, left: 0 }}
              borderColor={{
                from: 'color',
                modifiers: [['darker', 1]],
              }}
              enableTracks={false}
              enableCircularGrid={false}
              enableRadialGrid={false}
              // radialAxisStart={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
              radialAxisStart={{
                tickComponent: (props) => {
                  const { label, y, textX, rotation, textAnchor } = props;

                  return (
                    <g
                      opacity='1'
                      transform={`translate(${y}, 0) rotate(${rotation})`}
                    >
                      <line
                        x2={-5}
                        style={{ stroke: 'rgb(119, 119, 119)', strokeWidth: 1 }}
                      />
                      <text
                        dx={textX}
                        textAnchor={textAnchor}
                        dominantBaseline='central'
                        style={{
                          fontFamily: 'sans-serif',
                          fontSize: 11,
                          fill: 'rgb(155, 58, 58)',
                          outlineWidth: 0,
                          outlineColor: 'transparent',
                        }}
                      >
                        {label}
                      </text>
                    </g>
                  );
                },
              }}
              circularAxisOuter={{
                tickSize: 1,
                tickPadding: 10,
                tickRotation: 0,
              }}
              legends={[
                {
                  anchor: 'right',
                  direction: 'column',
                  justify: false,
                  translateX: 80,
                  translateY: 0,
                  itemsSpacing: 6,
                  itemDirection: 'left-to-right',
                  itemWidth: 100,
                  itemHeight: 18,
                  itemTextColor: '#999',
                  symbolSize: 18,
                  symbolShape: 'square',
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemTextColor: '#000',
                      },
                    },
                  ],
                },
              ]}
            />
          </ChartSizeFixer>
          {/* 배송원 변경 영역 */}
          <StyledH4>
            <div
              style={{
                width: '40px',
                textAlign: 'end',
                height: '30px',
                alignContent: 'center',
                cursor: 'pointer',
              }}
              onClick={() => changeWorkbayGroup(-1)}
            >
              <IoIosArrowBack />
            </div>
            <div
              style={{
                paddingBottom: '3px',
                fontSize: '18px',
                color: theme.colors.charcoal,
              }}
            >
              {
                workbayList.find(
                  (workbay) => workbay.id === selectedChartGroup?.workbayId
                )?.name
              }
            </div>
            <div
              style={{
                width: '40px',
                textAlign: 'start',
                height: '30px',
                alignContent: 'center',
                cursor: 'pointer',
              }}
              onClick={() => changeWorkbayGroup(1)}
            >
              <IoIosArrowForward />
            </div>
          </StyledH4>
        </>
      ) : (
        <StyledH4 style={{ height: '100%' }}>생산현황이 없습니다</StyledH4>
      )}
    </div>
  );
};

const ChartTooltip = styled.div`
  padding: 7px;
  background-color: white;
  border: 1px solid gray;
  border-radius: 8px;
`;

const ChartSizeFixer = styled.div`
  width: 100%;
  height: 250px;
  padding-right: 15px;
`;

const StyledH4 = styled.h4`
  margin: 0;
  left: 50%;
  transform: translateX(-50%);
  position: absolute;
  bottom: 15px;
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  white-space: nowrap;
`;
